import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ReportSettings } from 'store/ui/report/setup/types';

export type ReportConfigurationType = 'online' | 'powerpoint' | '';
export type ReportDefinition = {
	feeds: string[];
	feedClippings: ReportFeedClipping[];
	settings: ReportSettings;
};
export type ReportType = 'coverage' | 'periodic' | 'campaign' | 'excel';
export type FeedClippingSort = "reach" | "echo" | "miv" | "publication_date";
export type FeedClippingCount = 10 | 50 | 200 | 500;

export type ReportFeedClipping = { id: string, count: FeedClippingCount, sort?: FeedClippingSort};

export type ReportConfiguration = {
	type: ReportConfigurationType;
	id: string | null;
	name: string | null;
	definition: ReportDefinition;
	created_at?: string;
	updated_at?: string;
};

export type State = {
	reportError: string;
	saveReportModalOpen: boolean;
	configuration: ReportConfiguration;
};

export const INITIAL_STATE: State = {
	reportError: '',
	saveReportModalOpen: false,
	configuration: {
		id: null,
		name: null,
		type: '',
		definition: {
			feeds: [],
			feedClippings: [],
			settings: {
				type: 'coverage',
				period: 'monthly',
				language: 'en',
				currency: 'EUR',
				minDate: null,
				maxDate: null,
				country: 'all',
				groupedByCountry: false,
				topicTagIds: [],
				categories: [],
				topicTagOperator: 'OR'
			}
		}
	}
};

const slice = createSlice({
	name: 'report',
	initialState: INITIAL_STATE,
	reducers: {
		resetReport: () => INITIAL_STATE,
		setReportType: (state, action: PayloadAction<{type: ReportConfigurationType}>) => {
			state.configuration.type = action.payload.type;
		},
		setReportName: (state, action: PayloadAction<string>) => {
			state.configuration.name = action.payload;
		},
		setSaveReportModalOpen: (state, action: PayloadAction<boolean>) => {
			state.saveReportModalOpen = action.payload;
		},
		setReportSettings: (state, action: PayloadAction<Partial<ReportSettings>>) => {
			state.configuration.definition.settings = { ...state.configuration.definition.settings, ...action.payload };
		},
		setReportClipping: (state, action: PayloadAction<{feedId: string, count: FeedClippingCount}>) => {
			const newClippings = [...state.configuration.definition.feedClippings];
			for (const clipping of newClippings) {
				if (clipping.id === action.payload.feedId) {
					clipping.count = action.payload.count;
					state.configuration.definition.feedClippings = newClippings;
					return;
				}
			}
			state.configuration.definition.feedClippings.push({ id: action.payload.feedId, count: action.payload.count });
		},
		removeReportClipping: (state, action: PayloadAction<{feedId: string}>) => {
			state.configuration.definition.feedClippings = state.configuration.definition.feedClippings.filter(clipping => clipping.id !== action.payload.feedId);
		},
		setReportError: (state, action: PayloadAction<string>) => {
			state.reportError = action.payload;
		}
	}
});

export const {
	resetReport,
	setReportType,
	setReportName,
	setSaveReportModalOpen,
	setReportSettings,
	setReportError,
	setReportClipping,
	removeReportClipping
} = slice.actions;

export default slice.reducer;
